'use client';
import { useContext, useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import CollectionListItem from "../collections/collection-list-item";
import { ProductCardConfig } from "../collections/collection-list-item";
import "./product-highlight.css";
import { useProductLayout } from "@/lib/providers/product-layout-provider";

type ProductHighlightCarouselProps = {
    productList: any[];
    level?: any;
    shopCurrency: string;
    type?: "TYPE_1" | "TYPE_2";
    /** Number of items shown per row on mobile. Applies to "TYPE_1" (GRADIENT card) and "TYPE_2" (SOLID card). Defaults to 2. */
    mobileColumn?: number;
}

const ProductHighlightCarousel = (props: ProductHighlightCarouselProps) => {
    const { productList, level, shopCurrency, type, mobileColumn } = props;
    const supportsMobileColumn = type === "TYPE_1" || type === "TYPE_2";
    const resolvedMobileColumn = mobileColumn ?? 2;
    const useTightMobileGap = supportsMobileColumn && resolvedMobileColumn === 3;
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });
    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(true);
    const [selectedSnap, setSelectedSnap] = useState(0);
    const [snapCount, setSnapCount] = useState(0);
    const showMobileIndicator = supportsMobileColumn && resolvedMobileColumn === 3;

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setPrevDisabled(!emblaApi.canScrollPrev());
        setNextDisabled(!emblaApi.canScrollNext());
        setSelectedSnap(emblaApi.selectedScrollSnap());
        setSnapCount(emblaApi.scrollSnapList().length);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);
    
    const scrollNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };
    const scrollPrev = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    const productLayout = useProductLayout();
    const maxItem = productLayout.productColumns
    const arrowPosition = productLayout.arrowPosition

    return (
        <div id="product-highlight-component-carousel-container" className="w-full relative">
            <div id="product-highlight-component-carousel-embla" className="embla" ref={emblaRef}>
                <div
                    id="product-highlight-component-carousel-embla-container"
                    className={`embla__container product-highlight-carousel-embla${useTightMobileGap ? " product-highlight-gap-tight" : ""}`}
                    style={{ display: "flex", ...(useTightMobileGap ? { "--mobile-gap": "8px" } : {}) } as React.CSSProperties}
                >
                    {productList.map((product, index) => (
                        (product && product.productVariant && product.productVariant.length > 0) && (
                            <div
                                id={`product-highlight-component-carousel-item-${product.id}`}
                                key={product.id}
                                className={`${maxItem == 6 ? "product-highlight-6" : "product-highlight"}${supportsMobileColumn ? " product-highlight-mobile-custom" : ""} flex flex-col gap-5 flex-shrink-0`}
                                style={supportsMobileColumn ? ({ "--mobile-columns": resolvedMobileColumn } as React.CSSProperties) : undefined}
                            >
                                <CollectionListItem
                                    product={product}
                                    key={product.id}
                                    href={`/products/${product.handle}`}
                                    level={level}
                                    priorityImage={index < 2}
                                    shopCurrency={shopCurrency}
                                    cardType={type == "TYPE_1" ? ProductCardConfig.GRADIENT :
                                        type == "TYPE_2" ? ProductCardConfig.SOLID_IN_PRODUCT_HIGHLIGHT :
                                            ProductCardConfig.DEFAULT}
                                    mobileColumn={supportsMobileColumn ? resolvedMobileColumn : undefined}
                                />
                            </div>
                        )
                    ))}
                </div>
            </div>
            {/* Mobile carousel progress indicator — 3-column mobile layout only.
                Single track with a yellow segment that slides to the current index. */}
            {showMobileIndicator && snapCount > 1 && (
                <div
                    className="relative w-full mx-auto h-1 rounded-full mt-3 sm:hidden"
                    style={{ backgroundColor: "#F4F2F2" }}
                >
                    <div
                        className="absolute top-0 h-1 rounded-full transition-all duration-300"
                        style={{
                            width: `${100 / snapCount}%`,
                            left: `${(selectedSnap * 100) / snapCount}%`,
                            backgroundColor: "#FFC20E",
                        }}
                    />
                </div>
            )}
            {/* Navigation Buttons */}
            {arrowPosition == "SIDE" ? <>
                <button
                    id="product-highlight-component-carousel-button-prev"
                    aria-label="Previous"
                    className="
                        embla__button embla__button--prev inline-flex items-center justify-center whitespace-nowrap 
                        ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 
                        focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 
                        text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden dynamic-bg dynamic-color 
                        dynamic-border absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 shadow"
                    style={
                        {
                            "--dynamic-bg": "#ffffff",
                            "--dynamic-hover-bg": "#ffffff",
                            "--dynamic-border": "#B3B3B3",
                            "--dynamic-hover-border": "#FFC20E",
                            "--dynamic-color": "#5B595A",
                            "--dynamic-hover-color": "#5B595A",
                            "--dynamic-focus-color": "#5B595A",
                            "--dynamic-hover-focus-color": "#5B595A",
                        } as React.CSSProperties
                    }
                    onClick={scrollPrev}
                    disabled={prevDisabled}
                >
                    <ChevronLeft id="product-highlight-component-carousel-button-prev-icon" className="h-5 w-5" />
                </button>
                <button
                    id="product-highlight-component-carousel-button-next"
                    aria-label="Next"
                    className="
                        embla__button embla__button--next inline-flex items-center justify-center whitespace-nowrap 
                        ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 
                        focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 
                        text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden dynamic-bg dynamic-color 
                        dynamic-border absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 shadow"
                    style={
                        {
                            "--dynamic-bg": "#ffffff",
                            "--dynamic-hover-bg": "#ffffff",
                            "--dynamic-border": "#B3B3B3",
                            "--dynamic-hover-border": "#FFC20E",
                            "--dynamic-color": "#5B595A",
                            "--dynamic-hover-color": "#5B595A",
                            "--dynamic-focus-color": "#5B595A",
                            "--dynamic-hover-focus-color": "#5B595A",
                        } as React.CSSProperties
                    }
                    onClick={scrollNext}
                    disabled={nextDisabled}
                >
                    <ChevronRight id="product-highlight-component-carousel-button-next-icon" className="h-5 w-5" />
                </button>
            </>: 
            <div id="product-highlight-carousel-prev-next" className="w-full flex justify-end mt-[20px]">
                <button
                    id="product-highlight-component-carousel-button-prev"
                    aria-label="Previous"
                    className="
                        embla__button embla__button--prev inline-flex items-center justify-center whitespace-nowrap 
                        ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 
                        focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 
                        text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden dynamic-bg dynamic-color 
                        dynamic-border relative transform left-0 top-0 translate-y-0 mx-1"
                    style={
                        {
                            "--dynamic-bg": "#ffffff",
                            "--dynamic-hover-bg": "#ffffff",
                            "--dynamic-border": "#B3B3B3",
                            "--dynamic-hover-border": "#FFC20E",
                            "--dynamic-color": "#5B595A",
                            "--dynamic-hover-color": "#5B595A",
                            "--dynamic-focus-color": "#5B595A",
                            "--dynamic-hover-focus-color": "#5B595A",
                        } as React.CSSProperties
                    }
                    onClick={scrollPrev}
                    disabled={prevDisabled}
                >
                    <ChevronLeft id="product-highlight-component-carousel-button-prev-icon" className="h-5 w-5" />
                </button>
                <button
                    id="product-highlight-component-carousel-button-next"
                    aria-label="Next"
                    className="
                        embla__button embla__button--next inline-flex items-center justify-center whitespace-nowrap 
                        ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 
                        focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 
                        text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden dynamic-bg dynamic-color 
                        dynamic-border absolute relative transform left-0 top-0 translate-y-0 mx-1"
                    style={
                        {
                            "--dynamic-bg": "#ffffff",
                            "--dynamic-hover-bg": "#ffffff",
                            "--dynamic-border": "#B3B3B3",
                            "--dynamic-hover-border": "#FFC20E",
                            "--dynamic-color": "#5B595A",
                            "--dynamic-hover-color": "#5B595A",
                            "--dynamic-focus-color": "#5B595A",
                            "--dynamic-hover-focus-color": "#5B595A",
                        } as React.CSSProperties
                    }
                    onClick={scrollNext}
                    disabled={nextDisabled}
                >
                    <ChevronRight id="product-highlight-component-carousel-button-next-icon" className="h-5 w-5" />
                </button>
            </div>
            }
        </div>
    )
}


export default ProductHighlightCarousel